import {
	EvSelectOptionValueType,
	OptionType,
	SORT_ORDER,
	TableSortOption,
	TFilterOptions
} from '@evinced-private/ui-common';

import mapValues from 'lodash-es/mapValues';
import pickBy from 'lodash-es/pickBy';
import { isNullOrUndefined } from 'src/common/helpers/UtilHelper';
import { ConditionOperators } from 'src/common/types/graphql.generated';

import { buildTupleString } from './buildTupleString';
import { removeSelectAllOption } from './removeSelectAllOption';

type GetSearchFields<T> = T extends {
	textSearch?: { searchFields: Array<infer TextSearchFields> } | null;
}
	? TextSearchFields[]
	: never[];

type GetSortField<T> = T extends { sort?: { fields: Array<infer SortField> } | null }
	? SortField
	: never;

type GetSortFieldsMap<T> = Record<string, GetSortField<T>>;

type GetSortOrder<T> = T extends { sort?: { order?: infer SortOrder } | null } ? SortOrder : never;

type GetSortOrderMap<T> = Record<SORT_ORDER, GetSortOrder<T>>;

type ConstructorParams<T> = {
	searchFields?: GetSearchFields<T>;
	sortFieldsMap?: GetSortFieldsMap<T>;
	sortOrderMap?: GetSortOrderMap<T>;
};

export class QueryVariablesBuilder<QueryVariables> {
	// #region private
	private variables: QueryVariables;

	private searchFields?: GetSearchFields<QueryVariables>;

	private sortFieldsMap?: GetSortFieldsMap<QueryVariables>;

	private sortOrderMap?: GetSortOrderMap<QueryVariables>;

	private buildConditionParam(
		values?: EvSelectOptionValueType | EvSelectOptionValueType[] | EvSelectOptionValueType[][],
		operator?: ConditionOperators
	): {
		value: EvSelectOptionValueType[] | EvSelectOptionValueType[][];
	} {
		let value: EvSelectOptionValueType[] | EvSelectOptionValueType[][];
		if (!isNullOrUndefined(values)) {
			if (Array.isArray(values)) {
				value = values;
			} else {
				value = [values];
			}
		} else {
			value = [];
		}

		return { value, ...(operator ? { operator } : {}) };
	}

	// #endregion

	// #region public
	constructor(params?: ConstructorParams<QueryVariables>) {
		this.variables = {} as QueryVariables;
		this.searchFields = params?.searchFields;
		this.sortFieldsMap = params?.sortFieldsMap;
		this.sortOrderMap = params?.sortOrderMap;
	}

	addPaginationParams(page: number, pageSize: number): this {
		const offset = (page - 1) * pageSize;
		this.variables = {
			...this.variables,
			offset,
			limit: pageSize
		};

		return this;
	}

	addTextSearchParam(
		searchValue: string | string[],
		customSearchFields?: GetSearchFields<QueryVariables>,
		exact?: boolean
	): this {
		const currentSearchFields = customSearchFields || this.searchFields;
		if (searchValue.length && currentSearchFields) {
			const textSearchParam = {
				insensitive: true,
				searchFields: currentSearchFields,
				searchValues: [].concat(searchValue)
			};
			this.variables = {
				...this.variables,
				[exact ? 'textSearchExact' : 'textSearch']: textSearchParam
			};
		}
		return this;
	}

	addSortParam(sort?: TableSortOption, customSortFields?: string[]): this {
		const fieldsMap = this.sortFieldsMap;
		const orderMap = this.sortOrderMap;

		if (sort && orderMap) {
			let sortParam: {
				fields: GetSortField<QueryVariables>[];
				order: GetSortOrder<QueryVariables>;
			};

			const customSortFieldsMapped =
				customSortFields?.map((customField) => fieldsMap?.[customField])?.filter(Boolean) ?? [];

			if (customSortFieldsMapped.length) {
				sortParam = {
					fields: [...customSortFieldsMapped],
					order: orderMap[sort.order]
				};
			} else if (fieldsMap?.[sort?.dataField]) {
				sortParam = {
					fields: [fieldsMap[sort.dataField]],
					order: orderMap[sort.order]
				};
			}

			this.variables = {
				...this.variables,
				sort: sortParam
			};
		}
		return this;
	}

	addFilterParam(
		key: keyof QueryVariables,
		options?: OptionType[],
		caseInsensitive?: boolean
	): this {
		if (options?.length) {
			const values = removeSelectAllOption(options).flatMap((option) => {
				const original = option.value as string;
				if (!caseInsensitive) {
					return [original];
				}
				const lower = original.toLowerCase();
				return lower !== original ? [original, lower] : [original];
			});
			const filter = this.buildConditionParam(values);
			if (filter.value.length) {
				this.variables = {
					...this.variables,
					[key]: filter
				};
			}
		}

		return this;
	}

	// in contrast to other value types, for tuples we don't have separate fields for separate properties.
	// they are all stored under one property in form of [('team','frontend'), ('team','backend'), ('project','wfa'), ('project','platform')].
	// to filter by (team=frontend OR team=backend) AND (project=platform), we need to create an array of arrays like this:
	// [["('team','frontend')", "('team','backend')""], ["('project','platform')"]].
	// then we need to create a condition param object(using this.buildConditionParam), which includes:
	// 1. the array of arrays shown above
	// 2. operator - AND
	// in this case, the conditions inside each inner array will be combined by an OR, and their results will be combined by ANDs.
	// (the inner ORs can actually be ANDs but that will depend on what gql function you pass the result on this function, which is in the query file)
	addTupleFilterParam(
		key: keyof QueryVariables,
		optionsPerLabel?: TFilterOptions,
		operator?: ConditionOperators,
		caseInsensitive?: boolean
	): this {
		const optionsWithoutSelectAll = mapValues(optionsPerLabel ?? {}, removeSelectAllOption);
		const nonEmptyOptionsPerLabel = pickBy(
			optionsWithoutSelectAll,
			(optionsArray) => optionsArray?.length
		);

		const labelOptionsAsTupleStringArrays = Object.keys(nonEmptyOptionsPerLabel).map((labelKey) => {
			const selectedLabelOptions = nonEmptyOptionsPerLabel[labelKey];
			return selectedLabelOptions.flatMap(({ value }) => {
				const original = buildTupleString(labelKey, value.toString());
				if (!caseInsensitive) {
					return [original];
				}
				const lower = buildTupleString(labelKey, value.toString().toLowerCase());
				return lower !== original ? [original, lower] : [original];
			});
		});

		if (labelOptionsAsTupleStringArrays?.length) {
			const filter = this.buildConditionParam(labelOptionsAsTupleStringArrays, operator);
			if (filter.value.length) {
				this.variables = {
					...this.variables,
					[key]: filter
				};
			}
		}

		return this;
	}

	addConditionParam(
		key: keyof QueryVariables,
		values?: EvSelectOptionValueType | EvSelectOptionValueType[]
	): this {
		const conditionObj = this.buildConditionParam(values);
		if (conditionObj.value.length) {
			this.variables = {
				...this.variables,
				[key]: conditionObj
			};
		}

		return this;
	}

	addNeTupleFilterParam(
		key: keyof QueryVariables,
		labelsExcludedFromDashboard?: TFilterOptions
	): this {
		if (!labelsExcludedFromDashboard || Object.keys(labelsExcludedFromDashboard).length === 0) {
			return this;
		}

		const allExcludedTuples: string[] = Object.keys(labelsExcludedFromDashboard).flatMap(
			(labelKey) => {
				const selectedLabelOptions = labelsExcludedFromDashboard[labelKey];
				return selectedLabelOptions.map(({ value }) =>
					buildTupleString(labelKey, value.toString())
				);
			}
		);

		if (allExcludedTuples.length) {
			const filter = this.buildConditionParam([allExcludedTuples], ConditionOperators.Any);
			if (filter.value.length) {
				this.variables = {
					...this.variables,
					[key]: filter
				};
			}
		}

		return this;
	}

	getVariables(): QueryVariables {
		return this.variables;
	}

	// #endregion
}
